<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">用户管理</h1>
            <p class="list-page-description">按部门查看用户信息，统一处理账号与权限配置</p>
          </div>
        </div>
      </div>
    </template>

    <div class="flex gap-6">
    <!-- 部门树 -->
    <div class="w-64 flex-shrink-0">
      <div class="list-page-table-panel">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <h3 class="text-sm font-medium text-slate-700">部门列表</h3>
        </div>
        <div class="p-2 max-h-[600px] overflow-auto">
          <DeptTree
            :tree-data="deptTree"
            :selected-dept-id="selectedDeptId"
            :expanded-ids="expandedIds"
            @select="selectDept"
            @toggle="toggleDept"
          />
        </div>
      </div>
    </div>

    <!-- 右侧内容 -->
    <div class="flex-1">
      <!-- 操作按钮区域 -->
      <div class="action-toolbar">
        <button v-permission="'system:user:add'" @click="openAddModal" class="btn-primary">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          新增
        </button>
      </div>

      <!-- 表格区域 -->
      <div class="list-page-table-panel">
        <PageFilterBar
          shell-class="filter-bar-shell is-embedded"
          body-class="filter-bar-body is-panel"
          show-refresh
          :refresh-loading="loading"
          @refresh="loadUserList"
        >
          <div class="filter-bar-field is-wide relative">
            <input v-model="searchForm.username" type="text" placeholder="请输入用户名称" class="form-search-input form-control-sm w-full" @keyup.enter="handleSearch">
            <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <input v-model="searchForm.phone" type="text" placeholder="请输入手机号码" class="form-input form-control-sm filter-bar-field">
          <el-select v-model="searchForm.status" placeholder="请选择状态" class="form-select form-control-sm filter-bar-field">
            <el-option label="全部状态" value="" />
            <el-option label="正常" value="0" />
            <el-option label="停用" value="1" />
          </el-select>
          <template #searchActions>
            <button type="button" @click="handleSearch" class="btn-primary">搜索</button>
            <button type="button" @click="resetSearch" class="btn-secondary">重置</button>
          </template>
          <!-- 已选部门显示 -->
          <template #extra>
            <div v-if="selectedDeptId" class="flex items-center gap-2">
              <span class="text-sm text-slate-500">当前部门：</span>
              <span class="px-2 py-1 bg-primary-100 text-primary-700 rounded text-sm">{{ selectedDeptName }}</span>
              <button type="button" @click="clearDeptFilter" class="text-sm text-primary-600 hover:text-primary-800">清除</button>
            </div>
          </template>
        </PageFilterBar>
        <div class="data-table-shell">
        <!-- 骨架屏加载状态 -->
        <div v-if="loading" class="data-table-scroll p-4">
          <table class="data-table">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left w-10">
                  <div class="h-4 w-4 bg-slate-200 rounded"></div>
                </th>
                <th class="data-table-th text-left w-16">
                  <div class="h-4 w-4 bg-slate-200 rounded"></div>
                </th>
                <th class="data-table-th text-left">
                  <div class="h-4 w-20 bg-slate-200 rounded"></div>
                </th>
                <th class="data-table-th text-left">
                  <div class="h-4 w-20 bg-slate-200 rounded"></div>
                </th>
                <th class="data-table-th text-left">
                  <div class="h-4 w-20 bg-slate-200 rounded"></div>
                </th>
                <th class="data-table-th text-left">
                  <div class="h-4 w-16 bg-slate-200 rounded"></div>
                </th>
                <th class="data-table-th text-left">
                  <div class="h-4 w-20 bg-slate-200 rounded"></div>
                </th>
                <th class="data-table-th text-left">
                  <div class="h-4 w-12 bg-slate-200 rounded"></div>
                </th>
                <th class="data-table-th text-left">
                  <div class="h-4 w-24 bg-slate-200 rounded"></div>
                </th>
                <th class="data-table-th text-left w-32">
                  <div class="h-4 w-16 bg-slate-200 rounded"></div>
                </th>
              </tr>
            </thead>
            <tbody class="data-table-body data-table-skeleton">
              <tr v-for="n in 5" :key="n" class="animate-pulse">
                <td class="data-table-td"><div class="h-4 w-4 bg-slate-200 rounded"></div></td>
                <td class="data-table-td">
                  <div class="w-10 h-10 rounded-full bg-slate-200"></div>
                </td>
                <td class="data-table-td"><div class="h-4 w-16 bg-slate-200 rounded"></div></td>
                <td class="data-table-td"><div class="h-4 w-20 bg-slate-200 rounded"></div></td>
                <td class="data-table-td"><div class="h-4 w-20 bg-slate-200 rounded"></div></td>
                <td class="data-table-td"><div class="h-4 w-16 bg-slate-200 rounded"></div></td>
                <td class="data-table-td"><div class="h-4 w-24 bg-slate-200 rounded"></div></td>
                <td class="data-table-td"><div class="h-6 w-14 bg-slate-200 rounded-full"></div></td>
                <td class="data-table-td"><div class="h-4 w-24 bg-slate-200 rounded"></div></td>
                <td class="data-table-td">
                  <div class="flex items-center gap-1">
                    <div class="h-8 w-8 bg-slate-200 rounded"></div>
                    <div class="h-8 w-8 bg-slate-200 rounded"></div>
                    <div class="h-8 w-8 bg-slate-200 rounded"></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="data-table-scroll">
        <table class="data-table">
          <thead class="data-table-head">
            <tr>
              <th class="data-table-th text-left w-10">
                <input type="checkbox" v-model="selectAll" @change="handleSelectAll" class="form-check">
              </th>
              <th class="data-table-th text-left w-16">头像</th>
              <th class="data-table-th text-left">用户编号</th>
              <th class="data-table-th text-left">用户名称</th>
              <th class="data-table-th text-left">用户昵称</th>
              <th class="data-table-th text-left">部门</th>
              <th class="data-table-th text-left">手机号码</th>
              <th class="data-table-th text-left">状态</th>
              <th class="data-table-th text-left">创建时间</th>
              <th class="data-table-th text-left w-32">操作</th>
            </tr>
          </thead>
          <tbody class="data-table-body">
            <tr v-for="user in filteredUserList" :key="user.id" class="data-table-row">
              <td class="data-table-td">
                <input type="checkbox" v-model="user.selected" class="form-check">
              </td>
              <td class="data-table-td">
                <div class="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" :class="getAvatarBg(user)">
                  <img v-if="user.avatarUrl" :src="user.avatarUrl" class="w-full h-full object-cover">
                  <span v-else class="text-sm font-medium" :class="getAvatarTextColor(user)">{{ user.nickname?.charAt(0) }}</span>
                </div>
              </td>
              <td class="data-table-td text-sm text-slate-900">{{ user.id }}</td>
              <td class="data-table-td text-sm text-slate-900">{{ user.username }}</td>
              <td class="data-table-td text-sm text-slate-900">{{ user.displayName || user.nickname }}</td>
              <td class="data-table-td text-sm text-slate-900">{{ user.deptName || user.dept }}</td>
              <td class="data-table-td text-sm text-slate-900">{{ user.phone }}</td>
              <td class="data-table-td">
                <span class="status-badge" :class="user.status === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                  <span class="w-1.5 h-1.5 rounded-full mr-1" :class="user.status === 0 ? 'bg-green-500' : 'bg-red-500'"></span>
                  {{ user.status === 0 ? '正常' : '停用' }}
                </span>
              </td>
              <td class="data-table-td text-sm text-slate-900">{{ $formatDateTime(user.createTime) }}</td>
              <td class="data-table-td">
                <div class="flex items-center gap-1">
                  <button v-permission="'system:user:edit'" @click="openEditModal(user)" class="table-action-button is-primary" title="修改" aria-label="修改">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                  <button v-permission="'system:user:edit'" @click="handleDisable(user)" class="table-action-button" :class="user.status === 1 ? 'is-success' : 'is-neutral'" :title="user.status === 1 ? '启用' : '禁用'" :aria-label="user.status === 1 ? '启用' : '禁用'">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path v-if="user.status === 1" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                    </svg>
                  </button>
                  <button v-permission="'system:user:resetPwd'" @click="resetUserPassword(user)" class="table-action-button is-neutral" title="重置密码" aria-label="重置密码">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                  </button>
                  <button @click="openDataScopeModal(user)" class="table-action-button is-purple" title="数据权限" aria-label="数据权限">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                    </svg>
                  </button>
                  <button @click="openBindTenantModal(user)" class="table-action-button is-teal" title="分配加盟商" aria-label="分配加盟商">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                    </svg>
                  </button>
                  <button @click="openWeWorkBindModal(user)" class="table-action-button is-indigo" title="绑定企微" aria-label="绑定企微">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="filteredUserList.length === 0">
              <td colspan="10" class="data-table-empty text-slate-500">暂无数据</td>
            </tr>
          </tbody>
        </table>
        </div>
        </div>

        <AppPagination
          v-if="total > 0"
          class="pagination-bridge"
          :current="currentPage"
          :size="pageSize"
          :total="total"
          :pages="totalPages"
          :page-sizes="[10, 20, 50]"
          @page-change="handlePageChange"
          @size-change="handlePageSizeChange"
        />
      </div>
    </div>

    <!-- 新增/编辑用户弹窗 -->
    </div>
    <div v-if="showUserModal" class="fixed inset-0 z-50">
      <div class="fixed inset-0 bg-black/50" @click="closeUserModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col">
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
            <h3 class="text-lg font-semibold text-slate-900">{{ isEdit ? '编辑用户' : '新增用户' }}</h3>
            <button @click="closeUserModal" class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="flex-1 overflow-y-auto p-6">
            <form class="grid grid-cols-2 gap-4">
              <!-- 头像上传 -->
              <div class="col-span-2">
                <label class="block text-sm font-medium text-slate-700 mb-2">头像</label>
                <div class="flex items-center gap-4">
                  <div class="relative">
                    <div class="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden border-2 border-slate-300">
                      <span v-if="!userForm.avatar" class="text-2xl text-slate-400">{{ userForm.nickname?.charAt(0) || '用' }}</span>
                      <img v-else :src="userForm.avatar" class="w-full h-full object-cover">
                    </div>
                    <button type="button" @click="triggerAvatarUpload" class="absolute -bottom-1 -right-1 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center hover:bg-primary-700 transition-colors shadow-lg">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                      </svg>
                    </button>
                  </div>
                  <div class="flex-1">
                    <input type="file" ref="avatarInput" accept="image/*" @change="handleAvatarChange" class="hidden">
                    <button type="button" @click="triggerAvatarUpload" class="btn-secondary">选择图片</button>
                    <p class="text-xs text-slate-500 mt-1">支持 jpg、png 格式，大小不超过 2MB</p>
                  </div>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">用户昵称 <span class="text-red-500">*</span></label>
                <input v-model="userForm.nickname" type="text" required placeholder="请输入用户昵称" class="form-input w-full">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">用户名称 <span class="text-red-500">*</span></label>
                <input v-model="userForm.username" type="text" required placeholder="请输入用户名称" class="form-input w-full">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">部门 <span class="text-red-500">*</span></label>
                <div class="form-tree-panel max-h-56 overflow-auto">
                  <DeptTree
                    :tree-data="deptTree"
                    :selected-dept-id="userForm.deptId"
                    :expanded-ids="modalExpandedIds"
                    @select="selectDeptForUser"
                    @toggle="toggleModalDept"
                  />
                </div>
                <div class="mt-1 text-xs text-slate-500">
                  已选择：{{ selectedDeptName || '未选择' }}
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">手机号码 <span class="text-red-500">*</span></label>
                <input v-model="userForm.phone" type="text" required placeholder="请输入手机号码" class="form-input w-full">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">邮箱</label>
                <input v-model="userForm.email" type="email" placeholder="请输入邮箱" class="form-input w-full">
              </div>
              <div v-if="!isEdit">
                <label class="block text-sm font-medium text-slate-700 mb-1">密码 <span class="text-red-500">*</span></label>
                <input v-model="userForm.password" type="password" required placeholder="请输入密码" class="form-input w-full">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">性别</label>
                <el-select v-model="userForm.gender" class="form-select w-full">
                  <el-option label="男" value="0" />
                  <el-option label="女" value="1" />
                  <el-option label="未知" value="2" />
                </el-select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">状态</label>
                <div class="flex items-center gap-3">
                  <label class="form-switch">
                    <input type="checkbox" v-model="userForm.statusSwitch" class="form-switch-input" @change="updateStatusFromSwitch">
                    <span class="form-switch-track"></span>
                  </label>
                  <span class="text-sm text-slate-700">{{ userForm.status === 0 ? '正常' : '停用' }}</span>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">角色</label>
                <div class="relative">
                  <div @click="toggleRoleDropdown" :class="['form-selector-trigger w-full', { 'is-active': showRoleDropdown }]">
                    <div v-if="selectedRoles.length === 0" class="text-slate-400">请选择角色</div>
                    <span v-for="role in selectedRoles" :key="role.value" class="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs">
                      {{ role.label }}
                      <button type="button" @click.stop="removeRole(role.value)" class="hover:text-primary-900">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </span>
                  </div>
                  <div v-show="showRoleDropdown" class="form-dropdown-panel absolute z-10 w-full">
                    <label v-for="role in roleOptions" :key="role.value" class="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" :value="role.value" v-model="userForm.roles" class="role-checkbox form-check">
                      <span class="text-sm">{{ role.label }}</span>
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">岗位</label>
                <div class="relative">
                  <div @click="togglePositionDropdown" :class="['form-selector-trigger w-full', { 'is-active': showPositionDropdown }]">
                    <div v-if="selectedPositions.length === 0" class="text-slate-400">请选择岗位</div>
                    <span v-for="pos in selectedPositions" :key="pos.value" class="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                      {{ pos.label }}
                      <button type="button" @click.stop="removePosition(pos.value)" class="hover:text-green-900">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </span>
                  </div>
                  <div v-show="showPositionDropdown" class="form-dropdown-panel absolute z-10 w-full">
                    <label v-for="pos in positionOptions" :key="pos.value" class="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" :value="pos.value" v-model="userForm.positions" class="position-checkbox form-check">
                      <span class="text-sm">{{ pos.label }}</span>
                    </label>
                  </div>
                </div>
              </div>
              <div class="col-span-2">
                <label class="block text-sm font-medium text-slate-700 mb-1">备注</label>
                <textarea v-model="userForm.remark" rows="3" placeholder="请输入备注信息" class="form-textarea"></textarea>
              </div>
              <div v-if="showAssistantRelationPanel" class="col-span-2 border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div class="flex items-center justify-between mb-3">
                  <div>
                    <div class="text-sm font-medium text-slate-800">助理关系绑定</div>
                    <p class="text-xs text-slate-500 mt-0.5">绑定后，助理可查看所关联主用户的数据。</p>
                  </div>
                  <span v-if="assistantRelationLoading" class="text-xs text-slate-400">加载中...</span>
                </div>
                <div class="space-y-4">
                  <div v-for="config in activeAssistantRelationConfigs" :key="config.relationType">
                    <label class="block text-sm font-medium text-slate-700 mb-1">{{ config.label }}</label>
                    <el-select
                      v-model="assistantRelationForm[config.relationType]"
                      multiple
                      filterable
                      clearable
                      collapse-tags
                      collapse-tags-tooltip
                      :placeholder="config.placeholder"
                      class="w-full"
                    >
                      <el-option
                        v-for="option in getPrincipalOptions(config)"
                        :key="`${config.relationType}-${option.value}`"
                        :label="option.label"
                        :value="option.value"
                        :disabled="option.value === userForm.id"
                      />
                    </el-select>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 shrink-0">
            <button @click="closeUserModal" class="btn-secondary">取消</button>
            <button @click="saveUser" class="btn-primary">确定</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 禁用/启用确认弹窗 -->
    <div v-if="showDisableModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="showDisableModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4">
          <div class="flex items-center gap-4 mb-4">
            <div class="w-12 h-12 rounded-full flex items-center justify-center" :class="isUserDisabled ? 'bg-green-100' : 'bg-orange-100'">
              <svg class="w-6 h-6" :class="isUserDisabled ? 'text-green-600' : 'text-orange-600'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ isUserDisabled ? '确认启用' : '确认禁用' }}</h3>
              <p class="text-sm text-slate-500">{{ isUserDisabled ? '确定要启用该用户吗？启用后用户可正常登录系统。' : '确定要禁用该用户吗？禁用后用户将无法登录系统。' }}</p>
            </div>
          </div>
          <div class="flex items-center justify-end gap-3">
            <button @click="showDisableModal = false" class="btn-secondary">取消</button>
            <button @click="confirmDisable" class="px-4 py-2 text-white rounded-lg text-sm transition-colors" :class="isUserDisabled ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'">{{ isUserDisabled ? '启用' : '禁用' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 重置密码弹窗 -->
    <div v-if="showResetPwdModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="showResetPwdModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
          <div class="flex items-center gap-4 mb-5">
            <div class="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg class="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-slate-900">重置密码</h3>
              <p class="text-sm text-slate-500">用户：{{ resetPwdUser?.nickname || resetPwdUser?.username }}</p>
            </div>
          </div>
          <div class="mb-5">
            <label class="block text-sm font-medium text-slate-700 mb-1">新密码 <span class="text-red-500">*</span></label>
            <input
              v-model="resetPwdForm.newPassword"
              type="password"
              placeholder="请输入新密码"
              class="form-input w-full"
            >
            <p class="text-xs text-slate-400 mt-1.5">重置后用户需重新登录并修改默认密码</p>
          </div>
          <div class="flex items-center justify-end gap-3">
            <button @click="showResetPwdModal = false" class="btn-secondary">取消</button>
            <button @click="confirmResetPassword" :disabled="resetPwdLoading" class="btn-primary">
              {{ resetPwdLoading ? '重置中...' : '确认重置' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 用户权限配置弹窗（含 Tab: 菜单权限 / 数据权限） -->
    <div v-if="showDataScopeModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeDataScopeModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-3xl w-full">
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">权限配置</h3>
              <p class="text-sm text-slate-500 mt-0.5">用户：{{ dataScopeUser?.nickname || dataScopeUser?.username }}（优先级高于角色配置）</p>
            </div>
            <button @click="closeDataScopeModal" class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Tab 导航 -->
          <div class="px-6 border-b border-slate-200">
            <div class="flex gap-6">
              <button
                @click="permissionTab = 'menu'"
                class="py-3 text-sm font-medium border-b-2 transition-colors"
                :class="permissionTab === 'menu' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'"
              >菜单权限</button>
              <button
                @click="permissionTab = 'data'"
                class="py-3 text-sm font-medium border-b-2 transition-colors"
                :class="permissionTab === 'data' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'"
              >数据权限</button>
            </div>
          </div>

          <div class="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">

            <!-- ====== 菜单权限 Tab ====== -->
            <div v-show="permissionTab === 'menu'">
              <!-- 加载状态 -->
              <div v-if="userMenuLoading" class="text-center text-sm text-slate-500 py-8">
                <div class="flex items-center justify-center gap-2">
                  <svg class="w-5 h-5 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  加载菜单树...
                </div>
              </div>

              <div v-else>
                <!-- 菜单树 -->
                <div class="border border-slate-300 rounded-lg p-4 bg-slate-50 max-h-80 overflow-y-auto">
                  <div v-if="flattenedUserMenuTree.length === 0" class="text-sm text-slate-500 text-center py-4">暂无菜单数据</div>
                  <div v-for="item in flattenedUserMenuTree" :key="item.node.id" class="flex items-center gap-2 py-1" :style="{ paddingLeft: `${item.level * 16}px` }">
                    <button
                      v-if="item.node.children && item.node.children.length > 0"
                      type="button"
                      class="expand-btn p-0.5 hover:bg-slate-200 rounded transition-colors"
                      @click="toggleUserMenuExpand(item.node.id)"
                    >
                      <svg
                        class="w-4 h-4 text-slate-500 transition-transform"
                        :style="{ transform: userMenuExpandedIds.includes(item.node.id) ? 'rotate(90deg)' : 'rotate(0deg)' }"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                      </svg>
                    </button>
                    <span v-else class="w-4 h-4"></span>
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        class="form-check"
                        :checked="isUserMenuChecked(item.node)"
                        @change="onUserMenuCheck(item.node, $event)"
                      >
                      <span class="text-sm text-slate-700">{{ item.node.name }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- ====== 数据权限 Tab ====== -->
            <div v-show="permissionTab === 'data'">
              <!-- 加载状态 -->
              <div v-if="dataScopeLoading" class="text-center text-sm text-slate-500 py-8">
                <div class="flex items-center justify-center gap-2">
                  <svg class="w-5 h-5 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  加载资源列表...
                </div>
              </div>

              <!-- 空状态 -->
              <div v-else-if="userResourceScopes.length === 0" class="text-center text-sm text-slate-500 py-8">
                暂无可配置的资源
              </div>

              <!-- 资源权限表格 -->
              <div v-else class="border border-slate-300 rounded-lg overflow-hidden">
                <div class="overflow-x-auto max-h-96 overflow-y-auto">
                  <table class="w-full">
                    <thead class="bg-slate-50 sticky top-0 z-10">
                      <tr class="border-b border-slate-200">
                        <th class="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">资源名称</th>
                        <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">不设置</th>
                        <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">全部数据</th>
                        <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">本部门</th>
                        <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">本部门及以下</th>
                        <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">仅本人</th>
                        <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">自定义</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-slate-200">
                      <template v-for="(resource, index) in userResourceScopes" :key="resource.resourceCode">
                        <tr class="hover:bg-slate-50 transition-colors">
                          <td class="px-4 py-3">
                            <div class="flex flex-col">
                              <span class="text-sm font-medium text-slate-900">{{ resource.resourceName }}</span>
                              <span class="text-xs text-slate-500 font-mono">{{ resource.resourceCode }}</span>
                            </div>
                          </td>
                          <td class="px-4 py-3 text-center">
                            <label class="inline-flex items-center justify-center cursor-pointer">
                              <input type="radio" :name="`user-resource-${resource.resourceCode}`" :value="0" v-model.number="resource.dataScope" @change="onUserResourceDataScopeChange(index)" class="form-radio">
                            </label>
                          </td>
                          <td class="px-4 py-3 text-center">
                            <label class="inline-flex items-center justify-center cursor-pointer">
                              <input type="radio" :name="`user-resource-${resource.resourceCode}`" :value="1" v-model.number="resource.dataScope" @change="onUserResourceDataScopeChange(index)" class="form-radio">
                            </label>
                          </td>
                          <td class="px-4 py-3 text-center">
                            <label class="inline-flex items-center justify-center cursor-pointer">
                              <input type="radio" :name="`user-resource-${resource.resourceCode}`" :value="2" v-model.number="resource.dataScope" @change="onUserResourceDataScopeChange(index)" class="form-radio">
                            </label>
                          </td>
                          <td class="px-4 py-3 text-center">
                            <label class="inline-flex items-center justify-center cursor-pointer">
                              <input type="radio" :name="`user-resource-${resource.resourceCode}`" :value="3" v-model.number="resource.dataScope" @change="onUserResourceDataScopeChange(index)" class="form-radio">
                            </label>
                          </td>
                          <td class="px-4 py-3 text-center">
                            <label class="inline-flex items-center justify-center cursor-pointer">
                              <input type="radio" :name="`user-resource-${resource.resourceCode}`" :value="4" v-model.number="resource.dataScope" @change="onUserResourceDataScopeChange(index)" class="form-radio">
                            </label>
                          </td>
                          <td class="px-4 py-3 text-center">
                            <label class="inline-flex items-center justify-center cursor-pointer">
                              <input type="radio" :name="`user-resource-${resource.resourceCode}`" :value="5" v-model.number="resource.dataScope" @change="onUserResourceDataScopeChange(index)" class="form-radio">
                            </label>
                          </td>
                        </tr>
                        <!-- 自定义部门选择行 -->
                        <tr v-if="resource.dataScope === 5" class="bg-slate-50">
                          <td colspan="7" class="px-4 py-3">
                            <div class="flex items-center gap-2">
                              <span class="text-sm text-slate-600 whitespace-nowrap">选择部门：</span>
                              <button
                                type="button"
                                class="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm text-left hover:bg-white hover:border-primary-400 transition-colors flex items-center justify-between group bg-white"
                                @click="openUserDeptSelector(index)">
                                <span class="truncate" :class="resource.selectedDeptNames ? 'text-slate-900' : 'text-slate-400'">
                                  {{ resource.selectedDeptNames || '点击选择部门' }}
                                </span>
                                <svg class="w-4 h-4 text-slate-400 group-hover:text-primary-600 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                </svg>
                              </button>
                              <span v-if="resource.selectedDeptNames" class="text-xs text-slate-500 whitespace-nowrap">
                                已选 {{ resource.deptIds.length }} 个
                              </span>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
            <button @click="closeDataScopeModal" class="btn-secondary">取消</button>
            <button @click="saveUserPermissions" :disabled="dataScopeSaving" class="btn-primary">
              {{ dataScopeSaving ? '保存中...' : '确定' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 用户数据权限-部门选择器弹窗 -->
    <div v-if="showUserDeptSelector" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeUserDeptSelector"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-md w-full">
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 class="text-lg font-semibold text-slate-900">选择部门</h3>
            <button @click="closeUserDeptSelector" class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            <div class="border border-slate-300 rounded-lg p-4 bg-slate-50">
              <div v-if="deptTree.length === 0" class="text-sm text-slate-500 text-center py-4">
                暂无部门数据
              </div>
              <div v-else class="space-y-1">
                <div v-for="item in flattenedUserDeptTree" :key="item.node.id" class="flex items-center gap-2 py-1" :style="{ paddingLeft: `${item.level * 16}px` }">
                  <button
                    v-if="item.node.children && item.node.children.length > 0"
                    type="button"
                    class="p-0.5 hover:bg-slate-200 rounded transition-colors"
                    @click="toggleUserDeptExpand(item.node.id)"
                  >
                    <svg
                      class="w-4 h-4 text-slate-500 transition-transform"
                      :style="{ transform: userDeptExpandedIds[item.node.id] ? 'rotate(90deg)' : 'rotate(0deg)' }"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                  <span v-else class="w-4 h-4"></span>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      class="form-check"
                      :checked="userSelectedDeptIds.includes(item.node.id)"
                      @change="onUserDeptCheck(item.node.id, $event)"
                    >
                    <span class="text-sm text-slate-700">{{ item.node.name }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50">
            <button @click="closeUserDeptSelector" class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-100 transition-colors">取消</button>
            <button @click="confirmUserDeptSelection" class="btn-primary">确定</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 分配加盟商弹窗 -->
    <div v-if="showBindTenantModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeBindTenantModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-lg w-full">
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">分配加盟商</h3>
              <p class="text-sm text-slate-500 mt-0.5">用户：{{ bindTenantUser?.nickname || bindTenantUser?.username }}（{{ bindTenantUser?.phone }}）</p>
            </div>
            <button @click="closeBindTenantModal" class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6 space-y-4">
            <!-- 加盟商选择 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1">加盟商 <span class="text-red-500">*</span></label>
              <div class="relative">
                <input
                  v-model="tenantSearchKeyword"
                  type="text"
                  placeholder="搜索加盟商名称..."
                  class="form-input w-full"
                  @input="searchTenants"
                  @focus="showTenantDropdown = true"
                >
                <div v-if="showTenantDropdown && filteredTenantList.length > 0" class="form-dropdown-panel absolute z-10 w-full">
                  <button
                    v-for="t in filteredTenantList"
                    :key="t.id"
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm transition-colors"
                    :class="bindTenantForm.tenantId === t.id ? 'bg-primary-50 text-primary-700' : 'text-slate-700'"
                    @click="selectTenantOption(t)"
                  >
                    <div class="font-medium">{{ t.name }}</div>
                    <div v-if="t.majorRegion" class="text-xs text-slate-400">{{ t.majorRegion }}{{ t.minorRegion ? ' - ' + t.minorRegion : '' }}</div>
                  </button>
                </div>
                <div v-if="showTenantDropdown && tenantSearchKeyword && filteredTenantList.length === 0 && !tenantListLoading" class="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg p-3 text-sm text-slate-500 text-center">
                  无匹配的加盟商
                </div>
              </div>
              <div v-if="bindTenantForm.tenantId" class="mt-1.5 flex items-center gap-2">
                <span class="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">{{ bindTenantForm.tenantName }}</span>
                <button type="button" @click="clearTenantSelection" class="text-xs text-slate-400 hover:text-slate-600">清除</button>
              </div>
            </div>

            <!-- 部门选择 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1">部门 <span class="text-red-500">*</span></label>
              <div v-if="!bindTenantForm.tenantId" class="border border-slate-300 rounded-lg p-4 text-sm text-slate-400 text-center">
                请先选择加盟商
              </div>
              <div v-else-if="bindTenantDeptTree.length === 0" class="border border-slate-300 rounded-lg p-4 text-sm text-slate-400 text-center">
                该加盟商下暂无部门数据
              </div>
              <div v-else class="form-tree-panel max-h-48 overflow-auto">
                <DeptTree
                  :tree-data="bindTenantDeptTree"
                  :selected-dept-id="bindTenantForm.deptId"
                  :expanded-ids="modalExpandedIds"
                  @select="(dept) => bindTenantForm.deptId = dept.id"
                  @toggle="toggleModalDept"
                />
              </div>
              <div v-if="bindTenantForm.deptId" class="mt-1 text-xs text-slate-500">
                已选择：{{ findDeptById(bindTenantForm.deptId)?.name || '未选择' }}
              </div>
            </div>

            <!-- 昵称 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1">昵称（可选）</label>
              <input
                v-model="bindTenantForm.nickname"
                type="text"
                placeholder="在该加盟商中的昵称，不填则使用默认"
                class="form-input w-full"
              >
            </div>

            <!-- 角色 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1">角色</label>
              <div class="relative">
                <div @click="showBindTenantRoleDropdown = !showBindTenantRoleDropdown" :class="['form-selector-trigger w-full', { 'is-active': showBindTenantRoleDropdown }]">
                  <div v-if="bindTenantForm.roleIds.length === 0" class="text-slate-400">请选择角色</div>
                  <span v-for="roleId in bindTenantForm.roleIds" :key="roleId" class="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs">
                    {{ roleOptions.find(r => r.value === roleId)?.label || roleId }}
                    <button type="button" @click.stop="bindTenantForm.roleIds = bindTenantForm.roleIds.filter(id => id !== roleId)" class="hover:text-primary-900">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </span>
                </div>
                <div v-show="showBindTenantRoleDropdown" class="form-dropdown-panel absolute z-10 w-full">
                  <label v-for="role in roleOptions" :key="role.value" class="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" :value="role.value" v-model="bindTenantForm.roleIds" class="form-check">
                    <span class="text-sm">{{ role.label }}</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- 岗位 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1">岗位</label>
              <div class="relative">
                <div @click="showBindTenantPostDropdown = !showBindTenantPostDropdown" :class="['form-selector-trigger w-full', { 'is-active': showBindTenantPostDropdown }]">
                  <div v-if="bindTenantForm.postIds.length === 0" class="text-slate-400">请选择岗位</div>
                  <span v-for="postId in bindTenantForm.postIds" :key="postId" class="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                    {{ positionOptions.find(p => p.value === postId)?.label || postId }}
                    <button type="button" @click.stop="bindTenantForm.postIds = bindTenantForm.postIds.filter(id => id !== postId)" class="hover:text-green-900">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </span>
                </div>
                <div v-show="showBindTenantPostDropdown" class="form-dropdown-panel absolute z-10 w-full">
                  <label v-for="pos in positionOptions" :key="pos.value" class="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" :value="pos.value" v-model="bindTenantForm.postIds" class="form-check">
                    <span class="text-sm">{{ pos.label }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
            <button @click="closeBindTenantModal" class="btn-secondary">取消</button>
            <button @click="confirmBindTenant" :disabled="bindTenantLoading" class="btn-primary">
              {{ bindTenantLoading ? '提交中...' : '确定' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- 绑定企微弹窗 -->
    <div v-if="showWeWorkBindModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeWeWorkBindModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-md w-full">
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">绑定企业微信</h3>
              <p class="text-sm text-slate-500 mt-0.5">用户：{{ weWorkBindUser?.nickname || weWorkBindUser?.username }}</p>
            </div>
            <button @click="closeWeWorkBindModal" class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6">
            <!-- 加载状态 -->
            <div v-if="weWorkBindLoading" class="flex items-center justify-center py-8">
              <svg class="w-5 h-5 animate-spin text-primary-600 mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-sm text-slate-500">加载中...</span>
            </div>

            <!-- 已绑定状态 -->
            <div v-else-if="weWorkBindInfo" class="space-y-4">
              <div class="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div class="flex items-center gap-2 mb-3">
                  <span class="w-2 h-2 rounded-full bg-green-500"></span>
                  <span class="text-sm font-medium text-indigo-700">已绑定企业微信</span>
                </div>
                <div class="space-y-2 text-sm">
                  <div class="flex items-center justify-between">
                    <span class="text-slate-500">企微成员ID</span>
                    <span class="text-slate-900 font-mono">{{ weWorkBindInfo.weworkUserId }}</span>
                  </div>
                  <div v-if="weWorkBindInfo.corpId" class="flex items-center justify-between">
                    <span class="text-slate-500">企业ID</span>
                    <span class="text-slate-900 font-mono">{{ weWorkBindInfo.corpId }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-slate-500">状态</span>
                    <span class="status-badge" :class="weWorkBindInfo.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                      {{ weWorkBindInfo.status === 1 ? '启用' : '禁用' }}
                    </span>
                  </div>
                  <div v-if="weWorkBindInfo.bindTime" class="flex items-center justify-between">
                    <span class="text-slate-500">绑定时间</span>
                    <span class="text-slate-900">{{ $formatDateTime(weWorkBindInfo.bindTime) }}</span>
                  </div>
                </div>
              </div>
              <div class="flex items-center justify-end">
                <button @click="handleUnbindWeWork" :disabled="weWorkUnbindLoading" class="btn-danger-secondary">
                  {{ weWorkUnbindLoading ? '解绑中...' : '解除绑定' }}
                </button>
              </div>
            </div>

            <!-- 未绑定状态：绑定表单 -->
            <div v-else class="space-y-4">
              <div class="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div class="flex items-center gap-2 mb-2">
                  <span class="w-2 h-2 rounded-full bg-slate-400"></span>
                  <span class="text-sm font-medium text-slate-600">未绑定企业微信</span>
                </div>
                <p class="text-xs text-slate-500">绑定后可使用企业微信相关功能</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">企微成员UserID <span class="text-red-500">*</span></label>
                <input
                  v-model="weWorkBindForm.weworkUserId"
                  type="text"
                  placeholder="请输入企业微信成员UserID"
                  class="form-input w-full"
                >
                <p class="text-xs text-slate-400 mt-1">可在企业微信管理后台查看成员的UserID</p>
              </div>
              <div class="flex items-center justify-end gap-3">
                <button @click="closeWeWorkBindModal" class="btn-secondary">取消</button>
                <button @click="confirmBindWeWork" :disabled="weWorkSubmitLoading" class="btn-primary">
                  {{ weWorkSubmitLoading ? '绑定中...' : '确认绑定' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import DeptTree from './components/DeptTree.vue'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { getUserList, createUser, updateUser, disableUser, getUserDataScope, saveUserDataScope, deleteUserDataScope, getUserDirectMenuIds, grantUserDirectMenus, resetPassword, bindTenant, getAssistantRelations, saveAssistantRelation, disableAssistantRelation } from '@/api'
import { getAllDataScopeResources } from '@/api/role'
import { getDeptTree } from '@/api/dept'
import { getRoles } from '@/api/role'
import { getMenuTree } from '@/api/menu'
import { getPosts } from '@/api/post'
import { uploadFile } from '@/api/file'
import { compressImage } from '@/utils/imageCompress'
import { getFranchiseePage } from '@/api/franchisee'
import { getWeWorkBinding, bindWeWork, unbindWeWork } from '@/api/weworkKf'

const searchForm = reactive({
  username: '',
  phone: '',
  status: ''
})

const loading = ref(false)
const showUserModal = ref(false)
const showDisableModal = ref(false)
const isEdit = ref(false)
const selectedDeptId = ref(null)
const toggleStatusUser = ref(null)
const selectAll = ref(false)
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(50)
const totalPages = computed(() => Math.ceil(total.value / pageSize.value) || 1)
const avatarInput = ref(null)

const showRoleDropdown = ref(false)
const showPositionDropdown = ref(false)

// 展开状态对象 (使用普通对象，Vue 响应式追踪更可靠)
const expandedIds = reactive({})
const modalExpandedIds = reactive({})

const deptTree = ref([])

const findDeptById = (id, list = deptTree.value) => {
  if (!id) return null
  for (const item of list) {
    if (item.id === id) return item
    if (item.children?.length) {
      const found = findDeptById(id, item.children)
      if (found) return found
    }
  }
  return null
}

const findDeptIdByName = (name, list = deptTree.value) => {
  if (!name) return null
  for (const item of list) {
    if (item.name === name) return item.id
    if (item.children?.length) {
      const found = findDeptIdByName(name, item.children)
      if (found) return found
    }
  }
  return null
}

const roleOptions = ref([])
const positionOptions = ref([])

const userList = ref([])
const assistantPrincipalOptions = ref([])
const assistantRelationLoading = ref(false)

const ASSISTANT_RELATION_CONFIGS = [
  {
    assistantRoleKey: 'FA-ASSIST',
    principalRoleKeys: ['FA'],
    relationType: 'PLANNER_ASSISTANT',
    label: '关联规划师',
    placeholder: '请选择可查看数据的规划师'
  },
  {
    assistantRoleKey: 'FINANCE-ASSIST',
    principalRoleKeys: ['FINANCE-MANAGER', 'FINANCE-MAIN'],
    relationType: 'FINANCE_ASSISTANT',
    label: '关联主管会计',
    placeholder: '请选择可查看数据的主管会计'
  }
]

const assistantRelationForm = reactive({
  PLANNER_ASSISTANT: [],
  FINANCE_ASSISTANT: []
})

const originalAssistantRelationForm = reactive({
  PLANNER_ASSISTANT: [],
  FINANCE_ASSISTANT: []
})

const userForm = reactive({
  id: null,
  nickname: '',
  username: '',
  deptId: '',
  phone: '',
  email: '',
  password: '',
  gender: '0',
  status: 0,
  statusSwitch: false,
  roles: [],
  positions: [],
  remark: '',
  avatar: '',
  avatarKey: '',
  avatarFile: null
})

const filteredUserList = computed(() => {
  return userList.value
})

const selectedRoles = computed(() => {
  const normalizedRoleIds = userForm.roles.map(id => String(id))
  return roleOptions.value.filter(r => normalizedRoleIds.includes(String(r.value)))
})

const selectedRoleKeys = computed(() => {
  return selectedRoles.value.map(role => role.roleKey).filter(Boolean)
})

const activeAssistantRelationConfigs = computed(() => {
  return ASSISTANT_RELATION_CONFIGS.filter(config => selectedRoleKeys.value.includes(config.assistantRoleKey))
})

const showAssistantRelationPanel = computed(() => {
  return isEdit.value && activeAssistantRelationConfigs.value.length > 0
})

const selectedPositions = computed(() => {
  return positionOptions.value.filter(p => userForm.positions.includes(p.value))
})

const selectedDeptName = computed(() => {
  return findDeptById(selectedDeptId.value)?.name || ''
})

const getUserRoleIds = (user) => {
  return user.roleIds || user.roles || []
}

const getRoleKeysByIds = (roleIds = []) => {
  const normalizedRoleIds = roleIds.map(id => String(id))
  return roleOptions.value
    .filter(role => normalizedRoleIds.includes(String(role.value)))
    .map(role => role.roleKey)
    .filter(Boolean)
}

const formatUserOptionLabel = (user) => {
  const name = user.displayName || user.nickname || user.username || `用户${user.id}`
  const phone = user.phone ? `（${user.phone}）` : ''
  return `${name}${phone}`
}

const resetAssistantRelationForm = () => {
  Object.keys(assistantRelationForm).forEach(key => {
    assistantRelationForm[key] = []
    originalAssistantRelationForm[key] = []
  })
}

const getPrincipalOptions = (config) => {
  const availableUsers = assistantPrincipalOptions.value.filter(user => user.id !== userForm.id)
  const matchedUsers = availableUsers.filter(user => {
    const roleKeys = getRoleKeysByIds(getUserRoleIds(user))
    return roleKeys.some(roleKey => {
      if (config.relationType === 'PLANNER_ASSISTANT') {
        return roleKey !== 'FA-ASSIST' && roleKey.startsWith('FA')
      }
      return config.principalRoleKeys.includes(roleKey)
    })
  })
  const filtered = matchedUsers.length > 0 ? matchedUsers : availableUsers
  return filtered.map(user => ({
    value: user.id,
    label: formatUserOptionLabel(user)
  }))
}

const loadAssistantPrincipalOptions = async () => {
  try {
    const res = await getUserList({ page: 1, size: 500 })
    const records = Array.isArray(res?.records) ? res.records : []
    assistantPrincipalOptions.value = records.map(u => ({
      ...u,
      nickname: u.displayName || u.nickname,
      roleIds: u.roleIds || u.roles || [],
      postIds: u.postIds || u.positions || []
    }))
  } catch (error) {
    console.error('加载助理绑定候选用户失败:', error)
    assistantPrincipalOptions.value = []
  }
}

const loadAssistantRelations = async (assistantUserId) => {
  resetAssistantRelationForm()
  if (!assistantUserId) return
  assistantRelationLoading.value = true
  try {
    const [relations] = await Promise.all([
      getAssistantRelations(assistantUserId),
      assistantPrincipalOptions.value.length > 0 ? Promise.resolve() : loadAssistantPrincipalOptions()
    ])
    const list = Array.isArray(relations) ? relations : []
    const optionIds = new Set(assistantPrincipalOptions.value.map(user => user.id))
    ASSISTANT_RELATION_CONFIGS.forEach(config => {
      const ids = list
        .filter(item => item.relationType === config.relationType)
        .map(item => item.principalUserId)
        .filter(Boolean)
      ids.forEach(id => {
        if (!optionIds.has(id)) {
          assistantPrincipalOptions.value.push({
            id,
            nickname: `用户${id}`,
            username: `用户${id}`,
            roleIds: [],
            postIds: []
          })
          optionIds.add(id)
        }
      })
      assistantRelationForm[config.relationType] = [...ids]
      originalAssistantRelationForm[config.relationType] = [...ids]
    })
  } catch (error) {
    console.error('加载助理绑定关系失败:', error)
    ElMessage.error(error.message || '加载助理绑定关系失败')
  } finally {
    assistantRelationLoading.value = false
  }
}

const syncAssistantRelations = async () => {
  if (!isEdit.value || !userForm.id) return

  const activeRelationTypes = activeAssistantRelationConfigs.value.map(config => config.relationType)
  const tasks = []

  ASSISTANT_RELATION_CONFIGS.forEach(config => {
    const relationType = config.relationType
    const currentIds = activeRelationTypes.includes(relationType)
      ? assistantRelationForm[relationType]
      : []
    const originalIds = originalAssistantRelationForm[relationType]
    const addedIds = currentIds.filter(id => !originalIds.includes(id))
    const removedIds = originalIds.filter(id => !currentIds.includes(id))

    addedIds.forEach(principalUserId => {
      tasks.push(saveAssistantRelation({
        assistantUserId: userForm.id,
        principalUserId,
        relationType
      }))
    })
    removedIds.forEach(principalUserId => {
      tasks.push(disableAssistantRelation({
        assistantUserId: userForm.id,
        principalUserId,
        relationType
      }))
    })
  })

  if (tasks.length > 0) {
    await Promise.all(tasks)
  }
}

const selectDept = (dept) => {
  selectedDeptId.value = dept.id
  currentPage.value = 1
  loadUserList()
}

const clearDeptFilter = () => {
  selectedDeptId.value = null
  currentPage.value = 1
  loadUserList()
}

const handlePageChange = (page) => {
  currentPage.value = page
  loadUserList()
}

const handlePageSizeChange = (size) => {
  pageSize.value = size
  currentPage.value = 1
  loadUserList()
}

const toggleDept = (deptId) => {
  expandedIds[deptId] = !expandedIds[deptId]
}

const selectDeptForUser = (dept) => {
  userForm.deptId = dept.id
  selectedDeptId.value = dept.id
}

const toggleModalDept = (deptId) => {
  modalExpandedIds[deptId] = !modalExpandedIds[deptId]
}

const handleSearch = async () => {
  currentPage.value = 1
  await loadUserList()
}

const resetSearch = () => {
  searchForm.username = ''
  searchForm.phone = ''
  searchForm.status = ''
  loadUserList()
}

const handleSelectAll = () => {
  userList.value.forEach(u => u.selected = selectAll.value)
}

const isUserDisabled = computed(() => toggleStatusUser.value?.status === 1)

const handleDisable = (user) => {
  toggleStatusUser.value = user
  showDisableModal.value = true
}

const confirmDisable = async () => {
  try {
    loading.value = true
    await disableUser(toggleStatusUser.value.id)
    ElMessage.success(isUserDisabled.value ? '启用成功' : '禁用成功')
    showDisableModal.value = false
    loadUserList()
  } catch (error) {
    console.error(isUserDisabled.value ? '启用失败:' : '禁用失败:', error)
    ElMessage.error(error.message || (isUserDisabled.value ? '启用失败' : '禁用失败'))
  } finally {
    loading.value = false
  }
}

const showResetPwdModal = ref(false)
const resetPwdUser = ref(null)
const resetPwdLoading = ref(false)
const resetPwdForm = reactive({
  newPassword: ''
})

const resetUserPassword = (user) => {
  resetPwdUser.value = user
  resetPwdForm.newPassword = ''
  showResetPwdModal.value = true
}

const confirmResetPassword = async () => {
  if (!resetPwdForm.newPassword.trim()) {
    ElMessage.warning('请输入新密码')
    return
  }
  resetPwdLoading.value = true
  try {
    await resetPassword({
      userId: resetPwdUser.value.id,
      newPassword: resetPwdForm.newPassword
    })
    ElMessage.success('密码重置成功')
    showResetPwdModal.value = false
  } catch (error) {
    console.error('重置密码失败:', error)
    ElMessage.error(error.message || '重置密码失败')
  } finally {
    resetPwdLoading.value = false
  }
}

const openAddModal = () => {
  isEdit.value = false
  resetAssistantRelationForm()
  Object.assign(userForm, {
    id: null, nickname: '', username: '', deptId: '', phone: '', email: '', password: '',
    gender: '0', status: 0, statusSwitch: false, roles: [], positions: [], remark: '', avatar: '', avatarKey: '', avatarFile: null
  })
  showUserModal.value = true
}

const openEditModal = async (user) => {
  isEdit.value = true
  Object.assign(userForm, {
    id: user.id,
    nickname: user.nickname,
    username: user.username,
    deptId: user.deptId || findDeptIdByName(user.dept) || '',
    phone: user.phone,
    email: user.email || '',
    password: '',
    gender: String(user.gender || '0'),
    status: user.status,
    statusSwitch: user.status === 1,
    roles: user.roleIds || user.roles || [],
    positions: user.postIds || user.positions || [],
    remark: user.remark || '',
    avatar: user.avatar || '',
    avatarKey: '',
    avatarFile: null
  })
  showUserModal.value = true
  await loadAssistantRelations(user.id)
}

const closeUserModal = () => {
  showUserModal.value = false
}

const saveUser = async () => {
  // 表单验证
  if (!userForm.nickname.trim()) {
    ElMessage.warning('请输入用户昵称')
    return
  }
  if (!userForm.username.trim()) {
    ElMessage.warning('请输入用户名称')
    return
  }
  if (!userForm.deptId) {
    ElMessage.warning('请选择部门')
    return
  }
  if (!userForm.phone.trim()) {
    ElMessage.warning('请输入手机号码')
    return
  }
  if (!isEdit.value && !userForm.password) {
    ElMessage.warning('请输入密码')
    return
  }

  try {
    loading.value = true

    // 如果选择了头像，先压缩再上传
    if (userForm.avatarFile) {
      const compressed = await compressImage(userForm.avatarFile, { maxWidth: 400, maxHeight: 400, quality: 0.8 })
      const res = await uploadFile(compressed, 'avatar', 'public')
      userForm.avatarKey = res.fileName || res.data?.fileName
    }

    const data = {
      username: userForm.username,
      displayName: userForm.nickname,
      phone: userForm.phone,
      email: userForm.email || undefined,
      gender: parseInt(userForm.gender),
      status: userForm.status,
      deptId: userForm.deptId || undefined,
      roleIds: userForm.roles,
      postIds: userForm.positions,
      avatar: userForm.avatarKey || undefined
    }

    if (!isEdit.value) {
      data.password = userForm.password
      await createUser(data)
      ElMessage.success('添加成功')
    } else {
      await updateUser(userForm.id, data)
      await syncAssistantRelations()
      ElMessage.success('更新成功')
    }

    closeUserModal()
    loadUserList()
  } catch (error) {
    console.error('保存失败:', error)
    ElMessage.error(error.message || '保存失败')
  } finally {
    loading.value = false
  }
}

const triggerAvatarUpload = () => {
  avatarInput.value?.click()
}

const handleAvatarChange = (event) => {
  const file = event.target.files[0]
  if (file) {
    if (file.size > 2 * 1024 * 1024) {
      ElMessage.warning('图片大小不能超过 2MB')
      return
    }
    // 显示本地预览
    const reader = new FileReader()
    reader.onload = (e) => {
      userForm.avatar = e.target.result
    }
    reader.readAsDataURL(file)
    // 保存原始文件，用于后续上传
    userForm.avatarFile = file
  }
}

const updateStatusFromSwitch = () => {
  userForm.status = userForm.statusSwitch ? 1 : 0
}

const toggleRoleDropdown = () => {
  showRoleDropdown.value = !showRoleDropdown.value
  showPositionDropdown.value = false
}

const togglePositionDropdown = () => {
  showPositionDropdown.value = !showPositionDropdown.value
  showRoleDropdown.value = false
}

const removeRole = (value) => {
  const index = userForm.roles.indexOf(value)
  if (index > -1) userForm.roles.splice(index, 1)
  if (showAssistantRelationPanel.value) {
    loadAssistantPrincipalOptions()
  }
}

const removePosition = (value) => {
  const index = userForm.positions.indexOf(value)
  if (index > -1) userForm.positions.splice(index, 1)
}

const getAvatarBg = (user) => {
  const colors = ['bg-primary-100', 'bg-green-100', 'bg-red-100', 'bg-orange-100', 'bg-purple-100']
  const index = user.id % colors.length
  return colors[index]
}

const getAvatarTextColor = (user) => {
  const colors = ['text-primary-700', 'text-green-700', 'text-red-700', 'text-orange-700', 'text-purple-700']
  const index = user.id % colors.length
  return colors[index]
}

// 加载用户列表
const loadUserList = async () => {
  try {
    loading.value = true
    const params = {
      page: currentPage.value,
      size: pageSize.value
    }
    if (searchForm.username) params.username = searchForm.username
    if (searchForm.phone) params.phone = searchForm.phone
    if (searchForm.status) params.status = parseInt(searchForm.status)
    if (selectedDeptId.value) params.deptId = selectedDeptId.value

    const res = await getUserList(params)
    userList.value = res.records.map(u => ({
      ...u,
      selected: false,
      nickname: u.displayName || u.nickname,
      roleIds: u.roleIds || [],
      postIds: u.postIds || []
    }))
    total.value = res.total
  } catch (error) {
    console.error('加载用户列表失败:', error)
    ElMessage.error('加载用户列表失败')
  } finally {
    loading.value = false
  }
}

// 加载部门树
const loadDeptTree = async () => {
  try {
    const res = await getDeptTree()
    deptTree.value = res
    // 默认展开所有节点
    const expandAll = (list) => {
      list.forEach(item => {
        expandedIds[item.id] = true
        modalExpandedIds[item.id] = true
        if (item.children?.length) {
          expandAll(item.children)
        }
      })
    }
    if (res.length > 0) {
      expandAll(res)
    }
  } catch (error) {
    console.error('加载部门树失败:', error)
    ElMessage.error('加载部门树失败')
  }
}

// 加载角色列表
const loadRoles = async () => {
  try {
    const res = await getRoles()
    roleOptions.value = res.map(r => ({
      value: r.id,
      label: r.name,
      roleKey: r.roleKey || r.role_key
    }))
  } catch (error) {
    console.error('加载角色列表失败:', error)
  }
}

// 加载岗位列表
const loadPosts = async () => {
  try {
    const res = await getPosts()
    // 兼容分页数据和非分页数据，兼容不同字段名
    const list = Array.isArray(res) ? res : (res.records || [])
    positionOptions.value = list.map(p => ({
      value: p.id || p.postId,
      label: p.name || p.postName || p.code
    }))
  } catch (error) {
    console.error('加载岗位列表失败:', error)
  }
}

// 初始化
onMounted(async () => {
  await loadDeptTree()
  await loadUserList()
  await loadRoles()
  await loadPosts()
})

// 点击外部关闭下拉框
document.addEventListener('click', (e) => {
  if (!e.target.closest('.relative')) {
    showRoleDropdown.value = false
    showPositionDropdown.value = false
  }
})

// ========== 用户权限配置（菜单权限 + 数据权限） ==========
const showDataScopeModal = ref(false)
const dataScopeUser = ref(null)
const dataScopeLoading = ref(false)
const dataScopeSaving = ref(false)
const userResourceScopes = ref([])
const permissionTab = ref('menu')

// ---- 菜单权限 ----
const userMenuTree = ref([])
const userMenuExpandedIds = ref([])
const userMenuSelectedIds = ref([])
const userMenuLoading = ref(false)

// 扁平化菜单树
const flattenedUserMenuTree = computed(() => {
  const result = []
  const traverse = (nodes, level) => {
    if (!Array.isArray(nodes)) return
    nodes.forEach(node => {
      result.push({ node, level })
      if (node.children && Array.isArray(node.children) && userMenuExpandedIds.value.includes(node.id)) {
        traverse(node.children, level + 1)
      }
    })
  }
  traverse(userMenuTree.value, 0)
  return result
})

const toggleUserMenuExpand = (menuId) => {
  if (userMenuExpandedIds.value.includes(menuId)) {
    userMenuExpandedIds.value = userMenuExpandedIds.value.filter(id => id !== menuId)
  } else {
    userMenuExpandedIds.value = [...userMenuExpandedIds.value, menuId]
  }
}

const isUserMenuChecked = (node) => {
  return userMenuSelectedIds.value.includes(node.id)
}

// 收集所有有子节点的节点ID（用于默认展开）
const collectMenuExpandIds = (nodes) => {
  const ids = []
  if (!Array.isArray(nodes)) return ids
  nodes.forEach(node => {
    if (node.children && Array.isArray(node.children) && node.children.length > 0 && Number.isFinite(node.id)) {
      ids.push(node.id)
      ids.push(...collectMenuExpandIds(node.children))
    }
  })
  return ids
}

const onUserMenuCheck = (node, event) => {
  const checked = event.target.checked
  if (checked) {
    if (!userMenuSelectedIds.value.includes(node.id)) userMenuSelectedIds.value.push(node.id)
  } else {
    userMenuSelectedIds.value = userMenuSelectedIds.value.filter(id => id !== node.id)
  }
}

// 从接口返回的 menuIds 应用选中状态
const applyUserMenuSelection = (menuIds = []) => {
  const menuIdSet = new Set(menuIds)
  userMenuSelectedIds.value = []

  const traverse = (item) => {
    if (menuIdSet.has(item.id) && Number.isFinite(item.id)) {
      userMenuSelectedIds.value.push(item.id)
    }
    if (item.children && Array.isArray(item.children)) {
      item.children.forEach(traverse)
    }
  }
  userMenuTree.value.forEach(traverse)
}

// 收集所有选中的菜单ID
const collectUserMenuIds = () => {
  return [...userMenuSelectedIds.value]
}

// ---- 部门选择器相关 ----
const showUserDeptSelector = ref(false)
const userDeptExpandedIds = reactive({})
const userSelectedDeptIds = ref([])
const userDeptResourceIndex = ref(-1)

// 扁平化部门树（用于数据权限部门选择器）
const flattenedUserDeptTree = computed(() => {
  const result = []
  const traverse = (nodes, level) => {
    if (!Array.isArray(nodes)) return
    nodes.forEach(node => {
      result.push({ node, level })
      if (node.children && node.children.length > 0 && userDeptExpandedIds[node.id]) {
        traverse(node.children, level + 1)
      }
    })
  }
  traverse(deptTree.value, 0)
  return result
})

// 初始化部门选择器展开状态
const initUserDeptExpandedIds = () => {
  const expandAll = (list) => {
    list.forEach(item => {
      userDeptExpandedIds[item.id] = true
      if (item.children?.length) expandAll(item.children)
    })
  }
  expandAll(deptTree.value)
}

// 打开权限配置弹窗
const openDataScopeModal = async (user) => {
  dataScopeUser.value = user
  showDataScopeModal.value = true
  permissionTab.value = 'menu'
  dataScopeLoading.value = true
  userMenuLoading.value = true

  try {
    // 并行加载菜单树、用户菜单权限、资源列表、用户数据权限
    const [menuTreeData, userMenuIds, allResources, userScopes] = await Promise.all([
      getMenuTree(),
      getUserDirectMenuIds(user.id),
      getAllDataScopeResources(),
      getUserDataScope(user.id)
    ])

    // 菜单树
    userMenuTree.value = Array.isArray(menuTreeData) ? menuTreeData : []
    userMenuExpandedIds.value = collectMenuExpandIds(userMenuTree.value)
    const menuIds = Array.isArray(userMenuIds) ? userMenuIds : []
    applyUserMenuSelection(menuIds)
    userMenuLoading.value = false

    // 数据权限
    const enabledResources = Array.isArray(allResources)
      ? allResources.filter(r => r.status === 0)
      : []
    const existingScopes = Array.isArray(userScopes) ? userScopes : []

    userResourceScopes.value = enabledResources.map(resource => {
      const existing = existingScopes.find(s => s.resourceCode === resource.resourceCode)
      return {
        resourceCode: resource.resourceCode,
        resourceName: resource.resourceName,
        dataScope: existing?.dataScope || 0,
        deptIds: existing?.deptIds || [],
        selectedDeptNames: '',
        remark: existing?.remark || ''
      }
    })

    if (userResourceScopes.value.some(r => r.deptIds.length > 0)) {
      userResourceScopes.value.forEach((resource, index) => {
        if (resource.deptIds.length > 0) {
          updateUserResourceDeptNames(index)
        }
      })
    }
  } catch (error) {
    console.error('加载权限配置失败:', error)
    ElMessage.error('加载权限配置失败')
  } finally {
    dataScopeLoading.value = false
    userMenuLoading.value = false
  }
}

// 关闭权限配置弹窗
const closeDataScopeModal = () => {
  showDataScopeModal.value = false
  dataScopeUser.value = null
  userResourceScopes.value = []
  userMenuTree.value = []
  userMenuSelectedIds.value = []
  userMenuExpandedIds.value = []
}

// 资源数据范围变化
const onUserResourceDataScopeChange = (index) => {
  const resource = userResourceScopes.value[index]
  if (resource.dataScope !== 5) {
    resource.deptIds = []
    resource.selectedDeptNames = ''
  }
}

// 统一保存：菜单权限 + 数据权限
const saveUserPermissions = async () => {
  const userId = dataScopeUser.value?.id
  if (!userId) return

  // 校验自定义范围是否选了部门
  for (const resource of userResourceScopes.value) {
    if (resource.dataScope === 5 && resource.deptIds.length === 0) {
      ElMessage.warning(`资源「${resource.resourceName}」选择了自定义范围，请选择部门`)
      permissionTab.value = 'data'
      return
    }
  }

  dataScopeSaving.value = true
  try {
    const promises = []

    // 1. 保存菜单权限
    promises.push(grantUserDirectMenus(userId, {
      userId,
      menuIds: collectUserMenuIds()
    }))

    // 2. 保存数据权限
    const toSave = userResourceScopes.value.filter(r => r.dataScope > 0)
    const toDelete = userResourceScopes.value.filter(r => r.dataScope === 0)

    for (const resource of toSave) {
      promises.push(saveUserDataScope({
        userId,
        resourceCode: resource.resourceCode,
        dataScope: resource.dataScope,
        deptIds: resource.dataScope === 5 ? resource.deptIds : [],
        remark: resource.remark || ''
      }))
    }

    for (const resource of toDelete) {
      promises.push(
        deleteUserDataScope(userId, resource.resourceCode).catch(() => {})
      )
    }

    await Promise.all(promises)
    ElMessage.success('权限保存成功')
    closeDataScopeModal()
  } catch (error) {
    console.error('保存权限失败:', error)
    ElMessage.error(error.message || '保存权限失败')
  } finally {
    dataScopeSaving.value = false
  }
}

// 更新资源的部门名称显示
const updateUserResourceDeptNames = (index) => {
  const resource = userResourceScopes.value[index]
  if (!resource.deptIds || resource.deptIds.length === 0) {
    resource.selectedDeptNames = ''
    return
  }
  const deptNames = []
  const findNames = (nodes) => {
    if (!Array.isArray(nodes)) return
    nodes.forEach(node => {
      if (resource.deptIds.includes(node.id)) deptNames.push(node.name)
      if (node.children?.length) findNames(node.children)
    })
  }
  findNames(deptTree.value)
  resource.selectedDeptNames = deptNames.join(', ')
}

// 打开部门选择器
const openUserDeptSelector = (index) => {
  userDeptResourceIndex.value = index
  const resource = userResourceScopes.value[index]
  userSelectedDeptIds.value = resource.deptIds ? [...resource.deptIds] : []
  initUserDeptExpandedIds()
  showUserDeptSelector.value = true
}

// 关闭部门选择器
const closeUserDeptSelector = () => {
  showUserDeptSelector.value = false
  userDeptResourceIndex.value = -1
  userSelectedDeptIds.value = []
}

// 切换部门展开
const toggleUserDeptExpand = (deptId) => {
  userDeptExpandedIds[deptId] = !userDeptExpandedIds[deptId]
}

// 部门复选框
const onUserDeptCheck = (deptId, event) => {
  if (event.target.checked) {
    if (!userSelectedDeptIds.value.includes(deptId)) {
      userSelectedDeptIds.value.push(deptId)
    }
  } else {
    userSelectedDeptIds.value = userSelectedDeptIds.value.filter(id => id !== deptId)
  }
}

// 确认部门选择
const confirmUserDeptSelection = () => {
  if (userDeptResourceIndex.value >= 0) {
    const resource = userResourceScopes.value[userDeptResourceIndex.value]
    resource.deptIds = [...userSelectedDeptIds.value]
    updateUserResourceDeptNames(userDeptResourceIndex.value)
  }
  closeUserDeptSelector()
}

// ========== 分配加盟商（租户） ==========
const showBindTenantModal = ref(false)
const bindTenantUser = ref(null)
const bindTenantLoading = ref(false)
const tenantListLoading = ref(false)
const tenantList = ref([])
const tenantSearchKeyword = ref('')
const showTenantDropdown = ref(false)
const showBindTenantRoleDropdown = ref(false)
const showBindTenantPostDropdown = ref(false)
const bindTenantForm = reactive({
  tenantId: null,
  tenantName: '',
  deptId: null,
  roleIds: [],
  postIds: [],
  nickname: ''
})

const filteredTenantList = computed(() => {
  if (!tenantSearchKeyword.value) return tenantList.value
  const keyword = tenantSearchKeyword.value.toLowerCase()
  return tenantList.value.filter(t => t.name?.toLowerCase().includes(keyword))
})

// 根据选中的加盟商tenantId过滤部门树
const bindTenantDeptTree = computed(() => {
  const tid = bindTenantForm.tenantId
  if (!tid) return []
  const filterTree = (nodes) => {
    if (!Array.isArray(nodes)) return []
    const result = []
    for (const node of nodes) {
      if (node.tenantId === tid) {
        result.push({ ...node, children: filterTree(node.children) })
      } else if (node.children?.length) {
        const filtered = filterTree(node.children)
        if (filtered.length > 0) {
          result.push({ ...node, children: filtered })
        }
      }
    }
    return result
  }
  return filterTree(deptTree.value)
})

const loadTenantList = async () => {
  tenantListLoading.value = true
  try {
    const res = await getFranchiseePage({ current: 1, size: 200, statuses: [3] })
    tenantList.value = res.records || []
  } catch (error) {
    console.error('加载加盟商列表失败:', error)
    ElMessage.error('加载加盟商列表失败')
  } finally {
    tenantListLoading.value = false
  }
}

const openBindTenantModal = (user) => {
  bindTenantUser.value = user
  Object.assign(bindTenantForm, {
    tenantId: null,
    tenantName: '',
    deptId: null,
    roleIds: [],
    postIds: [],
    nickname: ''
  })
  tenantSearchKeyword.value = ''
  showTenantDropdown.value = false
  showBindTenantRoleDropdown.value = false
  showBindTenantPostDropdown.value = false
  showBindTenantModal.value = true
  loadTenantList()
}

const closeBindTenantModal = () => {
  showBindTenantModal.value = false
  bindTenantUser.value = null
}

const selectTenantOption = (tenant) => {
  bindTenantForm.tenantId = tenant.id
  bindTenantForm.tenantName = tenant.name
  bindTenantForm.deptId = null
  tenantSearchKeyword.value = tenant.name
  showTenantDropdown.value = false
}

const clearTenantSelection = () => {
  bindTenantForm.tenantId = null
  bindTenantForm.tenantName = ''
  bindTenantForm.deptId = null
  tenantSearchKeyword.value = ''
}

const searchTenants = () => {
  showTenantDropdown.value = true
}

const confirmBindTenant = async () => {
  if (!bindTenantForm.tenantId) {
    ElMessage.warning('请选择加盟商')
    return
  }
  if (!bindTenantForm.deptId) {
    ElMessage.warning('请选择部门')
    return
  }

  bindTenantLoading.value = true
  try {
    await bindTenant({
      tenantId: bindTenantForm.tenantId,
      phone: bindTenantUser.value.phone,
      deptId: bindTenantForm.deptId,
      roleIds: bindTenantForm.roleIds.length > 0 ? bindTenantForm.roleIds : undefined,
      postIds: bindTenantForm.postIds.length > 0 ? bindTenantForm.postIds : undefined,
      nickname: bindTenantForm.nickname || undefined
    })
    ElMessage.success('分配加盟商成功')
    closeBindTenantModal()
  } catch (error) {
    console.error('分配加盟商失败:', error)
    ElMessage.error(error.message || '分配加盟商失败')
  } finally {
    bindTenantLoading.value = false
  }
}

// ========== 绑定企业微信 ==========
const showWeWorkBindModal = ref(false)
const weWorkBindUser = ref(null)
const weWorkBindInfo = ref(null)
const weWorkBindLoading = ref(false)
const weWorkSubmitLoading = ref(false)
const weWorkUnbindLoading = ref(false)
const weWorkBindForm = reactive({
  weworkUserId: ''
})

const openWeWorkBindModal = async (user) => {
  weWorkBindUser.value = user
  weWorkBindInfo.value = null
  weWorkBindForm.weworkUserId = ''
  showWeWorkBindModal.value = true
  weWorkBindLoading.value = true
  try {
    const res = await getWeWorkBinding(user.id)
    if (res && res.weworkUserId) {
      weWorkBindInfo.value = res
    }
  } catch {
    // 未绑定时接口可能返回错误，忽略
  } finally {
    weWorkBindLoading.value = false
  }
}

const closeWeWorkBindModal = () => {
  showWeWorkBindModal.value = false
  weWorkBindUser.value = null
  weWorkBindInfo.value = null
}

const confirmBindWeWork = async () => {
  if (!weWorkBindForm.weworkUserId.trim()) {
    ElMessage.warning('请输入企业微信成员UserID')
    return
  }
  weWorkSubmitLoading.value = true
  try {
    await bindWeWork({
      userId: weWorkBindUser.value.id,
      weworkUserId: weWorkBindForm.weworkUserId.trim()
    })
    ElMessage.success('绑定成功')
    // 刷新绑定信息
    const res = await getWeWorkBinding(weWorkBindUser.value.id)
    if (res && res.weworkUserId) {
      weWorkBindInfo.value = res
    }
  } catch (error) {
    console.error('绑定企微失败:', error)
    ElMessage.error(error.message || '绑定失败')
  } finally {
    weWorkSubmitLoading.value = false
  }
}

const handleUnbindWeWork = async () => {
  weWorkUnbindLoading.value = true
  try {
    await unbindWeWork(weWorkBindUser.value.id)
    ElMessage.success('解绑成功')
    weWorkBindInfo.value = null
  } catch (error) {
    console.error('解绑企微失败:', error)
    ElMessage.error(error.message || '解绑失败')
  } finally {
    weWorkUnbindLoading.value = false
  }
}
</script>
